import { useEffect, useState, useRef } from 'react';
import './AnimatedCounter.css';

/**
 * AnimatedCounter - Componente per animare numeri da 0 al valore finale
 *
 * @param {number} value - Valore finale da raggiungere
 * @param {number} duration - Durata animazione in ms (default: 800)
 * @param {number} decimals - Numero di decimali (default: 0)
 * @param {string} suffix - Suffisso da aggiungere (es. "h", "%")
 * @param {string} prefix - Prefisso da aggiungere (es. "€")
 * @param {string} className - Classe CSS aggiuntiva
 * @param {string} trend - "up" | "down" | "neutral" per mostrare freccia trend
 * @param {string} trendValue - Valore del trend (es. "+5%")
 */
const AnimatedCounter = ({
    value,
    duration = 800,
    decimals = 0,
    suffix = '',
    prefix = '',
    className = '',
    trend = null,
    trendValue = ''
}) => {
    const [displayValue, setDisplayValue] = useState(0);
    const startTime = useRef(null);
    const animationFrame = useRef(null);
    const prevValue = useRef(value);

    useEffect(() => {
        // Se il valore è cambiato, riavvia l'animazione
        const startValue = prevValue.current !== value ? 0 : displayValue;
        prevValue.current = value;

        const animate = (timestamp) => {
            if (!startTime.current) startTime.current = timestamp;
            const progress = Math.min((timestamp - startTime.current) / duration, 1);

            // Easing function (ease-out)
            const easeOut = 1 - Math.pow(1 - progress, 3);
            const currentValue = startValue + (value - startValue) * easeOut;

            setDisplayValue(currentValue);

            if (progress < 1) {
                animationFrame.current = requestAnimationFrame(animate);
            }
        };

        startTime.current = null;
        animationFrame.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrame.current) {
                cancelAnimationFrame(animationFrame.current);
            }
        };
    }, [value, duration]);

    const formatNumber = (num) => {
        return num.toLocaleString('it-IT', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    const getTrendIcon = () => {
        if (!trend) return null;

        if (trend === 'up') {
            return (
                <span className="animated-counter-trend animated-counter-trend-up" title={trendValue}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="18 15 12 9 6 15"></polyline>
                    </svg>
                    {trendValue && <span className="animated-counter-trend-value">{trendValue}</span>}
                </span>
            );
        } else if (trend === 'down') {
            return (
                <span className="animated-counter-trend animated-counter-trend-down" title={trendValue}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                    {trendValue && <span className="animated-counter-trend-value">{trendValue}</span>}
                </span>
            );
        } else {
            return (
                <span className="animated-counter-trend animated-counter-trend-neutral" title="Stabile">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                </span>
            );
        }
    };

    return (
        <span className={`animated-counter ${className}`}>
            <span className="animated-counter-value">
                {prefix}{formatNumber(displayValue)}{suffix}
            </span>
            {getTrendIcon()}
        </span>
    );
};

export default AnimatedCounter;
